import React, { useState, useEffect } from "react";
import { Select, Form, Radio, Alert } from "antd";
import { useLocation } from "react-router";
import { useQuery } from "@apollo/client";
import { useTranslation } from "react-i18next";
// graphql
import GET_PROGRAMS from "graphql/queries/GetPrograms";

// common
import { ModalLayout, MoreModalButtons } from "common/modal";
import {
  ProgramSelectorContainer,
  ProgramOption,
  ProgramInfoContainer,
  ProgramDescription,
  Price,
  CurrencySign,
  PerUnitTime,
} from "common/program_selector";

// styling
// types
import { RadioChangeEvent } from "antd/lib/radio";
import { FormInstance } from "antd/lib/form";
import { Program } from "types";
import getCurrencySign from "utils/currency";
import resizeOverflowingPrice from "utils/resizeOverflowingPrice";
import { SelectValue } from "antd/lib/select";
import useLocale from "hooks/useLocale";
import { SUPPORTED_CURRENCIES } from "utils/constants";
import outputManuallySetPrice from "utils/outputManuallySetPrice";
import { ClassTypeSelector, CurrencySelector } from "./SelectProgramFormStyle";
import { FormCard, CardTitle, FormInfo } from "./RegistrationStyle";

const { Option } = Select;
const { ModalRow, ModalCol } = ModalLayout;
const { ButtonContainer, ModalButtonYellow } = MoreModalButtons;

type SelectProgramFormProps = {
  chosenProgram: Program;
  setChosenProgram: React.Dispatch<React.SetStateAction<Program>>;
  loading: boolean;
  showSubmitButton: boolean;
  form: FormInstance;
  currentLanguageId?: string;
};

const SelectProgramForm = ({
  chosenProgram,
  setChosenProgram,
  loading,
  showSubmitButton,
  form,
  currentLanguageId,
}: SelectProgramFormProps) => {
  const defaultProgramType =
    ["group", "1-on-1"].filter((type: string) =>
      chosenProgram?.filter.includes(type)
    )[0] || "group";
  const defaultCurrency = chosenProgram?.currency || "usd";

  const { t } = useTranslation("registration");
  const [programType, setProgramType] = useState(defaultProgramType);
  const [currency, setCurrency] = useState<SelectValue>(defaultCurrency);
  const language = useLocale();
  const { search } = useLocation();

  const {
    data: programsData,
    error: programsQueryError,
    loading: programsQueryLoading,
  } = useQuery(GET_PROGRAMS, {
    variables: {
      filter: [programType, "signup"],
      currency,
      language,
      studyLanguageId: currentLanguageId || "1",
    },
  });
  useEffect(() => {
    const urlParams = new URLSearchParams(search); // Not support IE
    const localeParam: string | null = urlParams.get("locale");
    if (localeParam) {
      const activeCurrency = SUPPORTED_CURRENCIES.find(({ locale }) => {
        return locale.includes(localeParam);
      });

      if (activeCurrency && activeCurrency.value !== "usd") {
        form.setFieldsValue({
          currency: activeCurrency.value,
        });
        setCurrency(activeCurrency.value);
      }
    }
  }, [search, form]);

  // Sort plans by price, DESC
  const plans = programsData?.plans
    .slice()
    .sort((plan1: Program, plan2: Program) => plan2.price - plan1.price);

  const renderProgramOptions = () => {
    if (programsQueryLoading) {
      return <Alert type="info" message={t("Loading...")} />;
    }

    if (programsQueryError) {
      return (
        <Alert
          type="error"
          message={t(
            "Error loading programs. Please check your internet or let us know if this keeps happening."
          )}
        />
      );
    }

    // if no plans found
    if (plans.length === 0) {
      return <Alert type="info" message={t("No program found.")} />;
    }

    return (
      <ProgramSelectorContainer>
        {plans.map((program: Program) => (
          <ProgramOption
            key={program.id}
            value={program.id}
            checked={chosenProgram.id === program.id}
            onChange={() => {
              setChosenProgram(program);
            }}
          >
            <ProgramInfoContainer>
              <ProgramDescription sm={16} xs={12}>
                <div style={{ whiteSpace: "normal" }}>{program.name}</div>
              </ProgramDescription>
              <Price
                sm={8}
                xs={12}
                style={{
                  fontSize: resizeOverflowingPrice(
                    outputManuallySetPrice(program.price)
                  ),
                }}
              >
                <CurrencySign>
                  <span // eslint-disable-next-line react/no-danger
                    dangerouslySetInnerHTML={{
                      __html: getCurrencySign(currency.toString()),
                    }}
                  />
                </CurrencySign>
                {outputManuallySetPrice(program.price)}
                <PerUnitTime>{t("/month")}</PerUnitTime>
              </Price>
            </ProgramInfoContainer>
          </ProgramOption>
        ))}
      </ProgramSelectorContainer>
    );
  };

  return (
    <FormCard style={{ maxWidth: 600 }}>
      <CardTitle>{t("Select program").toUpperCase()}</CardTitle>
      <ModalRow style={{ marginTop: "10px", justifyContent: "space-between" }}>
        <ModalCol xs={14} sm={14} md={10}>
          <Form.Item name="classType" initialValue="group">
            <ClassTypeSelector
              onChange={(ev: RadioChangeEvent) => {
                setProgramType(ev.target.value);
              }}
            >
              <Radio.Button value="group">{t("Group")}</Radio.Button>
              <Radio.Button value="1-on-1">{t("1-on-1")}</Radio.Button>
            </ClassTypeSelector>
          </Form.Item>
        </ModalCol>
        <ModalCol xs={8} sm={8} md={5}>
          <Form.Item name="currency" initialValue={defaultCurrency}>
            <CurrencySelector onChange={(value) => setCurrency(value)}>
              {SUPPORTED_CURRENCIES.map(({ name, value }) => (
                <Option value={value} key={value}>
                  {name}
                </Option>
              ))}
            </CurrencySelector>
          </Form.Item>
        </ModalCol>
      </ModalRow>

      <Form.Item
        name="program"
        rules={[{ required: true, message: t("You must select a program.") }]}
      >
        {renderProgramOptions()}
      </Form.Item>

      <FormInfo>
        {t("You can change or cancel your subscription at anytime")}.
      </FormInfo>

      {showSubmitButton && (
        <ModalRow>
          <ModalCol xs={0} sm={0} md={12} />
          <ButtonContainer xs={20} sm={20} md={12}>
            <Form.Item>
              <ModalButtonYellow
                type="primary"
                htmlType="submit"
                loading={loading}
              >
                {t("Sign up").toUpperCase()}
              </ModalButtonYellow>
            </Form.Item>
          </ButtonContainer>
        </ModalRow>
      )}
    </FormCard>
  );
};

export default SelectProgramForm;
